import { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import Iconify from 'src/components/iconify';
import { GoogleMapWrapper } from 'src/pages/app/devices/map-google';
import { LatLngLiteral, STOP_LABELS, Trip } from '../types';
import { GlyphMarker } from '../route-map';

function RoutePolyline({ path }: { path: google.maps.LatLng[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || path.length < 2) {
      return undefined;
    }
    const polyline = new google.maps.Polyline({
      path,
      strokeColor: '#288dff',
      strokeOpacity: 1,
      strokeWeight: 5,
    });
    polyline.setMap(map);

    const bounds = new google.maps.LatLngBounds();
    path.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 40);

    return () => {
      polyline.setMap(null);
    };
  }, [map, path]);

  return null;
}

type TrackMapProps = {
  trip: Trip;
  truckPosition: LatLngLiteral | null;
};

export default function TrackMap({ trip, truckPosition }: TrackMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const geometry = useMapsLibrary('geometry');

  const path = useMemo(() => {
    if (!geometry || !trip.routePolyline) {
      return [];
    }
    try {
      return geometry.encoding.decodePath(trip.routePolyline);
    } catch {
      return [];
    }
  }, [geometry, trip.routePolyline]);

  return (
    <Box
      component="div"
      sx={{ position: 'relative', width: '100%', height: 300, '& > div': { borderRadius: 1, overflow: 'hidden' } }}
    >
      <GoogleMapWrapper mapType="default" mapRef={mapRef}>
        <RoutePolyline path={path} />

        {truckPosition && (
          <GlyphMarker
            position={truckPosition}
            glyph={<Iconify icon="glyphs-poly:truck" width={20} />}
            title="Truck"
            size={34}
          />
        )}

        {trip.stops.map((stop, index) => (
          <GlyphMarker
            key={index}
            position={{ lat: stop.lat, lng: stop.lng }}
            glyph={
              <Iconify
                icon={stop.label === 'PICKUP' ? 'glyphs-poly:box-1' : 'glyphs-poly:flag'}
                width={17}
                style={{ opacity: stop.done ? 0.4 : 1 }}
              />
            }
            title={`${STOP_LABELS[stop.label]} ${index + 1}${stop.done ? ' · done' : ''}`}
            size={index === trip.nextStopIndex && !stop.done ? 34 : 28}
          />
        ))}
      </GoogleMapWrapper>
    </Box>
  );
}
